import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Country, GrowlBrokerService, GrowlType } from '@dsivd/prestations-ng';
import { Observable, of } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';

import { PersonNomenclature } from '../../model/person-nomenclature';
import { ResidencePermit } from '../../model/residence-permit';

export const REFINF_BASE_URL = 'api/refinf/';

@Injectable({
    providedIn: 'root'
})
export class RefInfService {

    private civilities: PersonNomenclature[];
    private readonly civilitiesObservable: Observable<PersonNomenclature[]>;

    private maritalStatuses: PersonNomenclature[];
    private readonly maritalStatusesObservable: Observable<PersonNomenclature[]>;

    private residencePermits: ResidencePermit[];
    private readonly residencePermitsObservable: Observable<ResidencePermit[]>;

    private countries: Country[];
    private readonly countriesObservable: Observable<Country[]>;

    constructor(
        private httpClient: HttpClient,
        private growlService: GrowlBrokerService
    ) {
        this.civilitiesObservable = this.httpClient
            .get<PersonNomenclature[]>(`${REFINF_BASE_URL}civilities`)
            .pipe(catchError((e: unknown) => {
                console.error(e);
                growlService.addWithType(
                    GrowlType.DANGER,
                    'Impossible de récupérer les civilités'
                );
                return of<PersonNomenclature[]>([]);
            }),
                  tap(civilities => {
                      this.civilities = civilities;
                  }),
                  shareReplay(1)
            );

        this.maritalStatusesObservable = this.httpClient
            .get<PersonNomenclature[]>(`${REFINF_BASE_URL}maritalStatuses`)
            .pipe(catchError((e: unknown) => {
                console.error(e);
                growlService.addWithType(
                    GrowlType.DANGER,
                    'Impossible de récupérer les états civils'
                );
                return of<PersonNomenclature[]>([]);
            }),
                  tap(maritalStatuses => {
                      this.maritalStatuses = maritalStatuses;
                  }),
                  shareReplay(1)
            );

        this.residencePermitsObservable = this.httpClient
            .get<ResidencePermit[]>(`${REFINF_BASE_URL}residencesPermit/shortList`)
            .pipe(catchError((e: unknown) => {
                console.error(e);
                growlService.addWithType(
                    GrowlType.DANGER,
                    'Impossible de récupérer les types de permis'
                );
                return of<ResidencePermit[]>([]);
            }),
                  tap(residencePermits => {
                      this.residencePermits = residencePermits;
                  }),
                  shareReplay(1)
            );

        this.countriesObservable = this.httpClient
            .get<Country[]>(`${REFINF_BASE_URL}countries`)
            .pipe(catchError((e: unknown) => {
                console.error(e);
                growlService.addWithType(
                    GrowlType.DANGER,
                    'Impossible de récupérer les pays'
                );
                return of<Country[]>([]);
            }),
                  tap(countries => {
                      this.countries = countries;
                  }),
                  shareReplay(1)
            );

    }

    getCivilities(): Observable<PersonNomenclature[]> {
        return this.civilitiesObservable;
    }

    getCivilitiesSync(): PersonNomenclature[] {
        if (!this.civilities) {
            throw new Error('Civilities not initialized.');
        }

        return this.civilities;
    }

    getMaritalStatuses(): Observable<PersonNomenclature[]> {
        return this.maritalStatusesObservable;
    }

    getMaritalStatusesSync(): PersonNomenclature[] {
        if (!this.maritalStatuses) {
            throw new Error('MaritalStatuses not initialized.');
        }

        return this.maritalStatuses;
    }

    getResidencePermits(): Observable<ResidencePermit[]> {
        return this.residencePermitsObservable;
    }

    getResidencePermitsSync(): ResidencePermit[] {
        if (!this.residencePermits) {
            throw new Error('ResidencePermits not initialized.');
        }

        return this.residencePermits;
    }

    getCountries(): Observable<Country[]> {
        return this.countriesObservable;
    }

    getCountriesSync(): Country[] {
        if (!this.countries) {
            throw new Error('Countries not initialized.');
        }

        return this.countries;
    }
}
